import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Badge,
  ProgressBar,
  Modal,
} from "react-bootstrap";
import NumberFormat from "react-number-format";
import {
  IconDeviceFloppy,
  IconArrowLeft,
  IconBox,
  IconPhoto,
  IconUpload,
  IconX,
  IconTrash,
  IconEye,
  IconAlertTriangle,
} from "@tabler/icons-react";
import Swal from "sweetalert2";

import ProductsServices from "../../../services/ProductsServices";
import { formatCurrencyBR, currencyToFloat } from "../../../helpers/Helpers";

const ProductForm = ({
  productId,
  onSuccess,
  onCancel,
  isInAgentHub = false,
  hubContext = null,
}) => {
  const isEditing = !!productId;
  const actuallyInAgentHub = isInAgentHub && !!hubContext;

  const [loading, setLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  const maxFiles = 10;
  const maxSize = 3 * 1024 * 1024; // 3MB
  const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      value: "",
      category: "",
      description: "",
      link: "",
      availability: "",
    },
  });

  useEffect(() => {
    if (isEditing) {
      loadProduct();
    }
  }, [isEditing, productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await ProductsServices.getProduct(productId);

      if (response.success) {
        const product = response.data;

        // Preencher o formulário
        reset({
          name: product.name || "",
          value: product.value ? formatCurrencyBR(product.value * 100) : "",
          category: product.category || "",
          description: product.description || "",
          link: product.link || "",
          availability: product.availability && product.availability !== -1 ? product.availability.toString() : "",
        });

        // Carregar imagens se existirem (direto da resposta da API)
        if (product.images && product.images.length > 0) {
          setUploadedImages(product.images);
        } else {
          setUploadedImages([]);
        }
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Erro ao carregar produto:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os dados do produto.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData) => {
    try {
      // Preparar dados para envio - sempre usar agentId do contexto
      if (!actuallyInAgentHub) {
        throw new Error("ProductForm deve ser usado apenas dentro do AgentHub");
      }

      const productData = {
        ...formData,
        value: typeof formData.value === 'string' ? currencyToFloat(formData.value) : formData.value,
        availability: formData.availability ? parseInt(formData.availability) : -1,
        agent: hubContext.agentId,
      };

      let response;

      // Configurar progresso se há imagens para upload
      if (selectedFiles.length > 0) {
        setUploading(true);
        setUploadProgress(0);

        // Simular progresso
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90));
        }, 100);

        // Limpar intervalo quando terminar
        var clearProgressInterval = () => {
          clearInterval(progressInterval);
          setUploadProgress(100);
          setTimeout(() => {
            setUploadProgress(0);
            setUploading(false);
          }, 1000);
        };
      }

      if (isEditing) {
        response = await ProductsServices.updateProduct(
          productId, 
          productData, 
          selectedFiles
        );

        if (response.success && selectedFiles.length > 0) {
          // Limpar arquivos selecionados
          previewImages.forEach((preview) => {
            URL.revokeObjectURL(preview.preview);
          });
          setSelectedFiles([]);
          setPreviewImages([]);

          // Recarregar as imagens do produto para evitar duplicação
          const updatedProduct = await ProductsServices.getProduct(
            productId
          );
          if (updatedProduct.success && updatedProduct.data.images) {
            setUploadedImages(updatedProduct.data.images);
          }

          if (selectedFiles.length > 0) {
            clearProgressInterval();
          }
        }
      } else {
        // Para criação, usar a nova implementação que envia tudo junto
        console.log('=== DEBUG CRIAÇÃO COM IMAGENS ===');
        console.log('Product Data:', productData);
        console.log('Selected Files for creation:', selectedFiles);
        console.log('Files details for creation:', selectedFiles.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          constructor: file.constructor.name
        })));
        
        response = await ProductsServices.createProduct(
          productData,
          selectedFiles
        );

        if (response.success) {
          // Limpar arquivos selecionados
          previewImages.forEach((preview) => {
            URL.revokeObjectURL(preview.preview);
          });
          setSelectedFiles([]);
          setPreviewImages([]);

          // As imagens já foram enviadas junto com o produto
          if (response.data.images) {
            setUploadedImages(response.data.images);
          }

          if (selectedFiles.length > 0) {
            clearProgressInterval();
          }
        }
      }
      console.log(response)
      console.log('caraiooooo')
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Sucesso!",
          text: `Produto ${isEditing ? "atualizado" : "criado"} com sucesso.`,
          timer: 2000,
          showConfirmButton: false,
        });

        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
         Swal.fire({
           icon: "error",
           title: response.error || "Oops!",
           text: response.errorMessage || `Não foi possível ${isEditing ? "atualizar" : "criar"} o produto.`,
           showConfirmButton: true,
         });
      }
    } catch (error) {
      console.error(
        `Erro ao ${isEditing ? "atualizar" : "criar"} produto:`,
        error
      );

      // Usar a nova função de tratamento de erros amigáveis
      const alertConfig = ProductsServices.showErrorAlert(
        error,
        `Erro ao ${isEditing ? "atualizar" : "criar"} produto`
      );

      Swal.fire(alertConfig);
    }
  };

  // Funções de manipulação de imagens integradas
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processSelectedFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    processSelectedFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const processSelectedFiles = (files) => {
    // Filtrar apenas arquivos de imagem
    const imageFiles = files.filter((file) => allowedTypes.includes(file.type));

    if (imageFiles.length !== files.length) {
      const alertConfig = ProductsServices.showErrorAlert(
        {
          response: {
            data: {
              error: "Arquivos inválidos",
              message:
                "Alguns arquivos foram ignorados. Apenas imagens JPG, JPEG e PNG são aceitas.",
              error_code: "INVALID_IMAGE_FILE",
            },
          },
        },
        "Seleção de Arquivos"
      );
      Swal.fire(alertConfig);
    }

    // Verificar limite de arquivos
    const totalImages =
      uploadedImages.length + selectedFiles.length + imageFiles.length;
    if (totalImages > maxFiles) {
      const alertConfig = ProductsServices.showErrorAlert(
        {
          response: {
            data: {
              error: "Limite de imagens atingido",
              message: `Máximo de ${maxFiles} imagens permitidas. Você já tem ${
                uploadedImages.length + selectedFiles.length
              } imagem(ns).`,
              error_code: "IMAGE_LIMIT_EXCEEDED",
            },
          },
        },
        "Limite de Imagens"
      );
      Swal.fire(alertConfig);
      return;
    }

    // Validar cada arquivo
    const validationResult = ProductsServices.validateProductImages(
      imageFiles,
      {
        maxFiles: maxFiles - uploadedImages.length - selectedFiles.length,
        maxSize,
        allowedTypes,
      }
    );

    if (!validationResult.isValid) {
      const errors = [
        ...validationResult.globalErrors,
        ...validationResult.results.flatMap((r) => r.errors),
      ];
      const alertConfig = ProductsServices.showErrorAlert(
        {
          response: {
            data: {
              error: "Erro na validação das imagens",
              message: errors.join("\n"),
              error_code: "VALIDATION_ERROR",
            },
          },
        },
        "Validação de Imagens"
      );
      Swal.fire(alertConfig);
      return;
    }

    // Criar previews e manter referência dos arquivos
    const newPreviews = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: Date.now() + Math.random(),
    }));

    // Armazenar os objetos File diretamente
    setSelectedFiles((prev) => [...prev, ...imageFiles]);
    setPreviewImages((prev) => [...prev, ...newPreviews]);
  };

  const removeSelectedFile = (index) => {
    const newSelectedFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previewImages.filter((_, i) => i !== index);

    // Limpar URL do preview
    if (previewImages[index]) {
      URL.revokeObjectURL(previewImages[index].preview);
    }

    setSelectedFiles(newSelectedFiles);
    setPreviewImages(newPreviews);
  };


  const deleteExistingImage = async (image) => {
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir esta imagem?`
    );
    if (confirmed) {
      try {
        const response = await ProductsServices.deleteProductImage(
          productId,
          image.id
        );
        if (response.success) {
          // Recarregar as imagens do produto para manter consistência
          const updatedProduct = await ProductsServices.getProduct(productId);
          if (updatedProduct.success && updatedProduct.data.images) {
            setUploadedImages(updatedProduct.data.images);
          } else {
            setUploadedImages([]);
          }
        }
      } catch (error) {
        console.error("Erro ao excluir imagem:", error);
      }
    }
  };

  const viewImage = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const validateUrl = (value) => {
    if (!value) return true; // Campo opcional

    try {
      new URL(value);
      return true;
    } catch {
      return "URL inválida";
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando dados do produto...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-form">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col lg={12}>
            <Row>
              {console.log(errors)}
              <Col md={8} className="mb-3">
                <Form.Label>
                  Nome do Produto <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: "Nome é obrigatório",
                    minLength: {
                      value: 3,
                      message: "Nome deve ter pelo menos 3 caracteres",
                    },
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder="Digite o nome do produto"
                      isInvalid={!!errors.name}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Col>

              <Col md={4} className="mb-3">
                <Form.Label>
                  Valor <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="value"
                  control={control}
                  rules={{
                    required: "Valor é obrigatório",
                    validate: (value) => {
                      // Verificar se o campo está vazio
                      if (!value || value.trim() === "") {
                        return "Valor é obrigatório";
                      }

                      // Converter valor formatado para float e validar
                      const num = currencyToFloat(value);
                      if (isNaN(num) || num <= 0) {
                        return "Valor deve ser maior que zero";
                      }
                      return true;
                    },
                  }}
                  render={({ field: { onChange, value, name, onBlur } }) => (
                    <NumberFormat
                      className={`form-control ${
                        errors.value ? "is-invalid" : ""
                      }`}
                      name={name}
                      placeholder="R$ 0,00"
                      value={value}
                      onBlur={onBlur}
                      prefix="R$ "
                      thousandSeparator="."
                      decimalSeparator=","
                      decimalScale={2}
                      fixedDecimalScale={true}
                      allowNegative={false}
                      onValueChange={(values) => {
                        const { formattedValue } = values;
                        onChange(formattedValue);
                      }}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.value?.message}
                </Form.Control.Feedback>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Label>Categoria</Form.Label>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder="Categoria do produto (opcional)"
                    />
                  )}
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Disponibilidade</Form.Label>
                <Controller
                  name="availability"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!value || value.trim() === "") return true; // Campo opcional
                      const num = parseInt(value);
                      if (isNaN(num) || num < 0) {
                        return "Disponibilidade deve ser um número positivo";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="number"
                      min="0"
                      placeholder="quantidade disponivel (opcional)"
                      isInvalid={!!errors.availability}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.availability?.message}
                </Form.Control.Feedback>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Label>Link</Form.Label>
                <Controller
                  name="link"
                  control={control}
                  rules={{
                    validate: validateUrl,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="url"
                      placeholder="https://exemplo.com (opcional)"
                      isInvalid={!!errors.link}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.link?.message}
                </Form.Control.Feedback>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Label>Descrição</Form.Label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      as="textarea"
                      rows={3}
                      placeholder="Descrição do produto (opcional)"
                    />
                  )}
                />
              </Col>
            </Row>

            {/* Seção de Imagens Integrada */}
            <hr className="my-4" />
            <Row>
              <Col md={12}>
                <h5 className="mb-3 d-flex align-items-center">
                  <IconPhoto size={20} className="me-2" />
                  Imagens do Produto
                </h5>

                <Alert variant="info" className="mb-3">
                  <small>
                    <strong>Limite:</strong> máximo {maxFiles} imagens de até
                    3MB cada (JPG, JPEG, PNG)
                  </small>
                </Alert>

                {/* Upload Area */}
                <Card className="mb-3">
                  <Card.Body>
                    <div
                      className="text-center py-4 border border-dashed rounded"
                      style={{
                        borderColor: "#dee2e6",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                      }}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <IconUpload size={48} className="text-muted mb-3" />
                      <h6>Adicionar Imagens</h6>
                      <p className="text-muted mb-3">
                        Arraste arquivos aqui ou clique para selecionar
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                        disabled={uploading}
                      />
                      <Button variant="outline-primary" disabled={uploading}>
                        {uploading ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <IconPhoto className="me-1" size={16} />
                        )}
                        Selecionar Imagens
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {/* Upload Progress */}
                {uploading && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">Fazendo upload...</small>
                      <small className="text-muted">{uploadProgress}%</small>
                    </div>
                    <ProgressBar now={uploadProgress} animated />
                  </div>
                )}

                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">
                        Arquivos Selecionados ({selectedFiles.length})
                      </h6>

                      {!isEditing && (
                        <small className="text-info">
                          As imagens serão enviadas junto com o produto
                        </small>
                      )}
                    </div>
                    <Row>
                      {previewImages.map((preview, index) => (
                        <Col
                          xs={6}
                          md={4}
                          lg={3}
                          key={preview.id}
                          className="mb-2"
                        >
                          <div className="position-relative">
                            <img
                              src={preview.preview}
                              alt={`Preview ${index + 1}`}
                              className="img-fluid rounded"
                              style={{
                                width: "100%",
                                height: "100px",
                                objectFit: "cover",
                              }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 m-1"
                              onClick={() => removeSelectedFile(index)}
                              style={{ padding: "4px 8px" }}
                            >
                              <IconX size={12} />
                            </Button>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* Existing Images */}
                {uploadedImages.length > 0 && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        Imagens do Produto ({uploadedImages.length})
                      </h6>
                      <Badge bg="success" className="d-flex align-items-center">
                        <IconPhoto size={14} className="me-1" />
                        {uploadedImages.length} imagem
                        {uploadedImages.length !== 1 ? "s" : ""}
                      </Badge>
                    </div>

                    <Row>
                      {uploadedImages.map((image, index) => (
                        <Col
                          xs={6}
                          md={4}
                          lg={3}
                          key={image.id}
                          className="mb-3"
                        >
                          <Card className="h-100">
                            <div className="position-relative">
                              <img
                                src={image.image_url || image.url}
                                alt={image.name || `Imagem ${index + 1}`}
                                className="card-img-top"
                                style={{
                                  height: "120px",
                                  objectFit: "cover",
                                  cursor: "pointer",
                                }}
                                onClick={() => viewImage(image)}
                              />
                              <div className="position-absolute top-0 end-0 m-1">
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => deleteExistingImage(image)}
                                  style={{ padding: "4px 8px" }}
                                >
                                  <IconTrash size={12} />
                                </Button>
                              </div>
                            </div>
                            <Card.Body className="p-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  {image.name || `Imagem ${index + 1}`}
                                </small>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => viewImage(image)}
                                  style={{ padding: "2px 6px" }}
                                >
                                  <IconEye size={12} />
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* No Images State */}
                {uploadedImages.length === 0 && selectedFiles.length === 0 && (
                  <Alert variant="secondary" className="text-center">
                    <IconPhoto size={24} className="mb-2" />
                    <p className="mb-0">Nenhuma imagem adicionada</p>
                    <small>Adicione imagens para enriquecer seu produto</small>
                  </Alert>
                )}

                {/* Limite Warning */}
                {uploadedImages.length + selectedFiles.length >= maxFiles && (
                  <Alert variant="warning" className="mt-3">
                    <IconAlertTriangle size={16} className="me-2" />
                    <small>Limite máximo de {maxFiles} imagens atingido.</small>
                  </Alert>
                )}
              </Col>
            </Row>
          </Col>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="outline-secondary" onClick={onCancel}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Salvando...
                </>
              ) : (
                <>
                  <IconDeviceFloppy size={16} className="me-1" />
                  {isEditing ? "Atualizar" : "Salvar"} Produto
                </>
              )}
            </Button>
          </div>
        </Row>
      </form>

      {/* Image Preview Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedImage?.name || "Visualizar Imagem"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage && (
            <img
              src={
                selectedImage.image_url ||
                selectedImage.url ||
                selectedImage.preview
              }
              alt={selectedImage.name || "Imagem"}
              className="img-fluid rounded"
              style={{ maxHeight: "70vh" }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductForm;
